import React, {lazy, Suspense} from "react";
import { createBrowserHistory } from "history";
import { Router, Route, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import "assets/scss/material-kit-react.scss?v=1.9.0";
import "assets/scss/material-table.scss?v=1.9.0";
import ProtectedRoute from './views/ProtectedRoute';
import {
  store,
  FirebaseProvider
} from "common";

import { features } from 'config';

const LandingPage = lazy(() => import("./views/LandingPage.js"));
const LoginPage = lazy(() => import("./views/LoginPage.js"));
const PrivacyPolicy = lazy(() => import("./views/PrivacyPolicy.js"));
const AboutUs = lazy(() => import("./views/AboutUs"));
const AuthLoading = lazy(() => import('./views/AuthLoading'));
const MyProfile = lazy(() => import('./views/MyProfile'));
const BookingHistory = lazy(() => import('./views/BookingHistory'));
const IncidentReports = lazy(() => import('./views/IncidentReports'));
const AppointmentBooking = lazy(() => import('./views/appointment-booking'));
const BookingHistoryAdvance = lazy(() => import('./views/BookingHistoryAdvance'));
const GuestUsers = lazy(() => import('./views/GuestUsers'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const CarTypes = lazy(() => import('./views/CarTypes'));
const Zones = lazy(() => import('./views/Zones'));
const SubZones = lazy(() => import('./views/SubZones'));
const CrowdedZones = lazy(() => import('./views/CrowdedZones'));
const AssignCrowdedZoneMap = lazy(() => import('./views/AssignCrowdedZoneMap'));
const IncrementHours = lazy(() => import('./views/IncrementHours'));
const AddBookings = lazy(() => import('./views/AddBookings'));
const AddBookingsGuest = lazy(() => import('./views/AddBookingsGuest'));
const RideDetailsGuest = lazy(() => import('./views/RideDetailsGuest'));
const CanceledBookingHistory = lazy(() => import('./views/CanceledBookingHistory'));
const Promos = lazy(() => import('./views/Promos'));
const Riders = lazy(() => import('./views/Riders'));
const Drivers = lazy(() => import('./views/Drivers'));
const DefaultFeedbacks = lazy(() => import('./views/DefaultFeedbacks'));
const DefaultChatMessages = lazy(() => import('./views/DefaultChatMessages'));
const BlockedDrivers = lazy(() => import('./views/BlockedDrivers'));
const BlockedRiders = lazy(() => import('./views/BlockedRiders'));
const FleetAdmins = lazy(() => import('./views/FleetAdmins'));
const Notifications = lazy(() => import('./views/Notifications'));
const DriverEarning = lazy(() => import('./views/DriverEarning'));
const Earningreports = lazy(() => import('./views/Earningreports'));
const AssignZoneMap = lazy(() => import('./views/AssignZoneMap'));
const AssignSubzoneMap = lazy(() => import('./views/AssignSubzoneMap'));
const AssignHoursMap = lazy(() => import('./views/AssignHoursMap'));
const Settings = lazy(() => import('./views/Settings'));
const Withdraws = lazy(() => import('./views/Withdraws'));
const CancellationReasons = lazy(() => import('./views/CancellationReasons'));
const RegisterPage = lazy(() => import('./views/RegisterPage'));
const AddMoney = lazy(() => import("./views/AddMoney"));
const TcUpdateWallets = lazy(() => import("./views/TcUpdateWallets"));
const UserVouchers = lazy(() => import("./views/UserVouchers"));
const DriverPromotions = lazy(() => import("./views/DriverPromotions"));
const Chat = lazy(() => import("./views/Chat"));
const WalletHistory = lazy(() => import("./views/WalletHistory"));
const RechargeCommission = lazy(() => import("./views/RechargeCommission"));
const Parameters = lazy(() => import("./views/Parameters"));
const DriverTaxesReport = lazy(() => import("./views/DriverTaxesReport"));
const AppSliders = lazy(() => import("./views/AppSliders"));
const TermsAndPrivacy = lazy(() => import("./views/TermsAndPrivacy"));
const DriverSuspension = lazy(() => import("./views/DriverSuspension"));
const DashboardAddresses = lazy(() => import("./views/DashboardAddresses"));
const PassengerDiscounts = lazy(() => import("./views/PassengerDiscounts"));
const DashboardBeta = lazy(() => import('./views/DashboardBeta'));
const PendingApprovalFieldsDrivers = lazy(() => import('./views/PendingApprovalFieldsDrivers'));
const DriverWorkSchedule = lazy(() => import('./views/DriverWorkSchedule'));
const ResetTwoStepVerification = lazy(() => import('./views/ResetTwoStepVerification'));
const DispatcherWorkSchedule = lazy(() => import('./views/DispatcherWorkSchedule'));
const ReferralSettings = lazy(() => import('./views/ReferralSettings'));

var hist = createBrowserHistory();

function App() {
  return (
    <Provider store={store}>
      <FirebaseProvider>
        <AuthLoading>
          <Router history={hist}>
            <Suspense fallback={<div>Loading...</div>}>
              <Switch>
                <Route component={AddBookingsGuest} path="/add-bookings-guest" />
                <Route component={RideDetailsGuest} path="/guest-ride-details/:idBooking" />
                <Route component={ResetTwoStepVerification} path="/two-step-verification-reset/:emailToReset" />
                <ProtectedRoute component={BookingHistoryAdvance} path="/bookingsadvance" permit={"admin,fleetadmin"} />
                <ProtectedRoute component={AppointmentBooking} path="/appointmentbooking" permit={"admin,fleetadmin"} />
                <ProtectedRoute component={DriverTaxesReport} path="/driverstaxes" permit={"accountant,admin"} />
                <ProtectedRoute exact component={Dashboard} path="/dashboard" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={DashboardBeta} path="/dashboard-beta" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={GuestUsers} path="/guestusers" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={BookingHistory} path="/bookings" permit={"rider,admin,fleetadmin"} />
                <ProtectedRoute exact component={IncidentReports} path="/incidentreports" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={MyProfile} path="/profile" permit={"rider,admin,driver,fleetadmin,accountant"} />
                <ProtectedRoute exact component={CarTypes} path="/cartypes" permit={"admin"} />
                <ProtectedRoute exact component={AssignZoneMap} path="/zonemap" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={AssignSubzoneMap} path="/subzonemap" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={AssignHoursMap} path="/hoursmap" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={CancellationReasons} path="/cancelreasons" permit={"admin"} />
                <ProtectedRoute exact component={AddBookings} path="/addbookings" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={Promos} path="/promos" permit={"admin"} />
                <ProtectedRoute exact component={Riders} path="/riders" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={Drivers} path="/drivers" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={PendingApprovalFieldsDrivers} path="/unapprove-drivers" permit={"admin"} />
                <ProtectedRoute exact component={DriverWorkSchedule} path="/drivers-work-schedule" permit={"admin"} />
                <ProtectedRoute exact component={WalletHistory} path="/walletHistory" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={Chat} path="/chat" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={BlockedDrivers} path="/blockeddrivers" permit={"admin"} />
                <ProtectedRoute exact component={BlockedRiders} path="/blockedriders" permit={"admin"} />
                <ProtectedRoute exact component={DefaultFeedbacks} path="/defaultfeedbacks" permit={"admin"} />
                <ProtectedRoute exact component={DefaultChatMessages} path="/defaultchatmessages" permit={"admin"} />
                <ProtectedRoute exact component={FleetAdmins} path="/fleetadmins" permit={"admin"} />
                <ProtectedRoute exact component={DriverEarning} path="/driverearning" permit={"admin"} />
                <ProtectedRoute exact component={Notifications} path="/notifications" permit={"admin"} />
                <ProtectedRoute exact component={Earningreports} path="/earningreports" permit={"admin"} />
                <ProtectedRoute exact component={AddMoney} path="/addtowallet" permit={"admin"} />
                <ProtectedRoute exact component={TcUpdateWallets} path="/tcupdatewallets" permit={"admin"} />
                <ProtectedRoute exact component={Withdraws} path="/withdraws" permit={"admin"} />
                <ProtectedRoute exact component={Zones} path="/zones" permit={"admin"} />
                <ProtectedRoute exact component={SubZones} path="/subzones" permit={"admin"} />
                <ProtectedRoute exact component={CrowdedZones} path="/crowdedzones" permit={"admin"} />
                <ProtectedRoute exact component={AssignCrowdedZoneMap} path="/crowdedzonemap" permit={"admin"} />
                <ProtectedRoute exact component={IncrementHours} path="/incrementhours" permit={"admin"} />
                <ProtectedRoute exact component={UserVouchers} path="/uservouchers" permit={"admin"} />
                <ProtectedRoute exact component={ReferralSettings} path="/referralsettings" permit={"admin"} />
                <ProtectedRoute exact component={DriverPromotions} path="/driverpromotions" permit={"admin"} />
                <ProtectedRoute exact component={Settings} path="/settings" permit={"admin"} />
                <ProtectedRoute exact component={Parameters} path="/parameters" permit={"admin"} />
                <ProtectedRoute exact component={RechargeCommission} path="/rechargecommission" permit={"admin"} />
                <ProtectedRoute exact component={AppSliders} path="/appsliders" permit={"admin"} />
                <ProtectedRoute exact component={TermsAndPrivacy} path="/termsandprivacy" permit={"admin"} />
                <ProtectedRoute exact component={DriverSuspension} path="/driversuspensions" permit={"admin"} />
                <ProtectedRoute exact component={DashboardAddresses} path="/saved-address" permit={"admin,fleetadmin"} />
                <ProtectedRoute exact component={PassengerDiscounts} path="/passengerdiscounts" permit={"admin"} />
                <ProtectedRoute exact component={DispatcherWorkSchedule} path="/dispatchers-work-schedule" permit={"admin,fleetadmin"} />
                <Route path="/about-us" component={AboutUs} />
                <Route path="/privacy-policy" component={PrivacyPolicy} />
                {features.WebsitePagesEnabled ?
                  <Route path="/register" component={RegisterPage} />
                  : null}
                {features.WebsitePagesEnabled ?
                  <Route path="/login" component={LoginPage} />
                  : null}
                {features.WebsitePagesEnabled ?
                  <Route path="/" component={LandingPage} />
                  :
                  <Route path="/" component={LoginPage} />
                }
              </Switch>
            </Suspense>
          </Router>
        </AuthLoading>
      </FirebaseProvider>
    </Provider>
  );
}

export default App;
